import React, { useContext, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, LogIn, Sparkles } from 'lucide-react';
import landingPage2 from '../media/landingPage2.jpeg';
import landingPage3 from '../media/landingPage3.jpeg';
import UniversalNavbar from '../components/UniversalNavbar';
import AuthContext from '../context/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const {loginUser, user} = useContext(AuthContext);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  if (user) {
    return <Navigate to="/homepage" />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await loginUser(e);
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="font-fell min-h-screen bg-gradient-to-br from-primary-light via-white to-primary-light/50">
      <UniversalNavbar />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-6rem)]">

            {/* Left Side - Login Form */}
            <motion.div
              className="w-full max-w-md mx-auto lg:mx-0"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Welcome Header */}
              <motion.div variants={itemVariants} className="text-center lg:text-left mb-8">
                <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary font-semibold text-sm mb-4">
                  <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
                  Your Designs, Their Demand
                </div>
                <h1 className="text-3xl lg:text-4xl font-bold text-dark mb-2">
                  Welcome Back to <span className="text-primary">VASSTR</span>
                </h1>
                <p className="text-muted text-lg">Sign in to manage your design portfolio</p>
              </motion.div>

              {/* Login Form */}
              <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                  >
                    <Alert variant="error" icon>
                      {error}
                    </Alert>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    icon={<Mail size={18} />}
                    required
                  />

                  <Input
                    type={passwordVisible ? "text" : "password"}
                    name="password"
                    id="password"
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    icon={<Lock size={18} />}
                    iconRight={
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="text-[var(--color-gray-500)] hover:text-[var(--color-gray-700)] transition-colors"
                      >
                        {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                    required
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                        Remember me
                      </label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:text-primary-dark font-medium transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <div className="space-y-4">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={isLoading}
                      icon={<LogIn size={20} />}
                      iconPosition="right"
                    >
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[var(--color-gray-300)]" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-[var(--color-gray-500)]">Don't have an account?</span>
                      </div>
                    </div>

                    <Link to="/Register">
                      <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        fullWidth
                        icon={<Sparkles size={20} />}
                      >
                        Create Account
                      </Button>
                    </Link>
                  </div>
                </form>

                {/* Contact Information */}
                <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600 mb-3">Need help? Contact our support team</p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center text-sm">
                    <a
                      href="mailto:team@vasstr.com"
                      className="text-primary hover:text-primary-dark font-medium transition-colors"
                    >
                      team@vasstr.com
                    </a>
                    <span className="hidden sm:inline text-gray-400">•</span>
                    <span className="text-gray-600">+91 97043 87878</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Visual Content */}
            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative">
                {/* Main visual */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent rounded-3xl transform rotate-3"></div>
                  <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-700">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-dark mb-2">Start Designing Today</h3>
                      <p className="text-muted mb-6">Join thousands of designers already growing their business</p>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-primary">💼</div>
                          <div className="text-xs text-muted">Build Portfolio</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-primary">🎨</div>
                          <div className="text-xs text-muted">Creative Freedom</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-primary">💰</div>
                          <div className="text-xs text-muted">Earn More</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Design Gallery */}
                <div className="grid grid-cols-2 gap-4">
                  <motion.div
                    className="relative group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={'https://d3qvi06vol9hrb.cloudfront.net/static/media/landingPage2.0ee2e356a70cba7cc13b.jpeg'}
                      alt="Designer showcase"
                      className="w-full h-48 object-cover rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 text-white">
                        <div className="text-sm font-semibold">Custom Designs</div>
                        <div className="text-xs">Premium Quality</div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    className="relative group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={'https://d3qvi06vol9hrb.cloudfront.net/static/media/landingPage3.834a5864c4b58c8b57f7.jpeg'}
                      alt="Fashion portfolio"
                      className="w-full h-48 object-cover rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 text-white">
                        <div className="text-sm font-semibold">Fashion Portfolio</div>
                        <div className="text-xs">Trending Styles</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;